import { sortRecords } from "@smartsuite/fields-logic/lib/sort";
import { getPrimaryField } from "@smartsuite/fields-logic/lib/utils/fields";
import {
  AppField,
  AppStructureFieldParams,
  FieldChoiceValue,
  Fields,
  Filter,
  FilterField,
  Preferences,
  SimpleRecord,
  SortRule,
  Tag,
} from "@smartsuite/types";
import {
  BaseRecord,
  FormConfigApplicationStructureItem,
  FormConfigItem,
} from "../../../../types/form";
import { getDisplayValue } from "../display-value/getFieldDisplayValue";

interface SelectedFormValues {
  [key: string]: Array<{ id: string }>;
}

// The resolved value is assigned back into FilterField.value by
// resolveDynamicFilterValues: dynamic rules resolve to record-id arrays,
// while empty values pass through unchanged.
type ResolvedFilterValue = string | string[] | null | undefined;

export const convertFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): ResolvedFilterValue => {
  if (!filterField.value) {
    return filterField.value;
  }

  if (Array.isArray(filterField.value)) {
    return convertArrayFilterValue(filterField, selectedFormValues);
  } else {
    return convertStringFilterValue(filterField, selectedFormValues);
  }
};

export const convertArrayFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): string[] =>
  filterField?.value?.reduce((acc: string[], oldValue: string) => {
    if (selectedFormValues[oldValue]) {
      return [...acc, ...selectedFormValues[oldValue].map((v) => v.id)];
    }
    return acc;
  }, []);

export const convertStringFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): string[] => {
  const dynamicTextFilterVarRegex = /(\[\[[a-z0-9_]+\]\])/g;

  const tokens = filterField.value.split(dynamicTextFilterVarRegex);

  return tokens.reduce((acc: string[], token: string) => {
    if (dynamicTextFilterVarRegex.test(token)) {
      const value = token.slice(2, -2);
      const selectedValueId = selectedFormValues[value]?.map((v) => v.id);
      if (selectedValueId) {
        return [...acc, ...selectedValueId];
      }
    } else {
      if (token) {
        return [...acc, token];
      }
    }

    return acc;
  }, []);
};

/**
 * Resolves dynamic filter values in a Filter object by substituting
 * form field references with their current values.
 */
export const resolveDynamicFilterValues = (
  filter: Filter,
  formValues: SelectedFormValues
): Filter => ({
  ...filter,
  fields: filter.fields.map((filterField) => ({
    ...filterField,
    value: filterField?.is_dynamic_value
      ? convertFilterValue(filterField, formValues)
      : filterField.value,
  })),
});

interface GetSortedRecordsParams {
  records: SimpleRecord[];
  formItem: FormConfigItem;
  field: FormConfigApplicationStructureItem;
  linkedApplicationFields?: {
    [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
  };
  preferences: Preferences;
}

export const getSortedRecords = ({
  records,
  formItem,
  field,
  linkedApplicationFields,
  preferences,
}: GetSortedRecordsParams): SimpleRecord[] => {
  const sortData = getSortData(formItem, field);

  const subItemsFields = Object.values(linkedApplicationFields ?? {}).filter(
    (field) => field.field_type === "subitemsfield"
  );

  const parseRecords = (records: SimpleRecord[]): SimpleRecord[] => {
    if (subItemsFields.length === 0) {
      return records;
    }

    return records.map((record) => {
      const subItems = subItemsFields.reduce(
        (acc, field) => ({
          ...acc,
          [field.slug]: { count: record[field.slug]?.items?.length || 0 },
        }),
        {}
      );

      return { ...record, ...subItems };
    });
  };

  const parsedRecords = parseRecords(records);

  const sortedRecords =
    sortData && parsedRecords.length && linkedApplicationFields
      ? sortRecords(
          parsedRecords,
          Object.values(linkedApplicationFields ?? {}),
          sortData,
          preferences
        )
      : records;

  return sortedRecords;
};

// Field types whose getDisplayValue returns a non-textual sentinel that would pollute
// free-text search (socialnetworkfield resolves to the literal "socialnetworkfield").
const SEARCH_SKIP_FIELD_TYPES = new Set<Fields>(["socialnetworkfield"]);

/**
 * Builds a single lowercased searchable string for a record: its title plus the
 * display value of each visible field. Used to let the select-record modal search
 * match visible field content, not only the title.
 */
export const buildRecordSearchText = (
  record: SimpleRecord,
  fieldsToShow: { [key: string]: AppField },
  tags?: Tag[],
  relatedRecords?: BaseRecord[]
): string => {
  const parts: string[] = [];

  if (record.title) {
    parts.push(record.title);
  }

  Object.entries(fieldsToShow).forEach(([fieldSlug, appField]) => {
    if (SEARCH_SKIP_FIELD_TYPES.has(appField?.field_type)) {
      return;
    }

    const value = getDisplayValue(record, fieldSlug, fieldsToShow, tags, relatedRecords);

    if (value) {
      parts.push(value.toString());
    }
  });

  return parts.join(" ").toLowerCase();
};

const MAX_VISIBLE_FIELDS = 4;

export const getVisibleFields = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem
): string[] => {
  const visibleFields: string[] =
    formItem.params.visible_fields ??
    field.params?.select_record_modal_visible_fields ??
    field.params?.visible_fields ??
    [];

  return visibleFields.filter((field) => field !== "title").slice(0, MAX_VISIBLE_FIELDS);
};

export const ensurePrimaryFirst = (slugs: string[], primarySlug: string): string[] => [
  primarySlug,
  ...slugs.filter((s) => s !== primarySlug),
];

export const filterValidSlugs = (slugs: string[], structureSlugs: Set<string>): string[] =>
  slugs.filter((slug) => structureSlugs.has(slug));

export const getTableVisibleFields = (
  formItem: FormConfigItem,
  linkedApplicationFields?: {
    [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
  }
): string[] => {
  if (!linkedApplicationFields) return [];

  const allFields = Object.values(linkedApplicationFields);
  const primary = getPrimaryField(allFields);
  const primarySlug = primary?.slug ?? "title";
  const structureSlugs = new Set(allFields.map((f) => f.slug));

  const raw = formItem.params.table_visible_fields ?? [];
  const filtered = filterValidSlugs(raw, structureSlugs);
  const safe = ensurePrimaryFirst(filtered, primarySlug);

  return safe.length > 0 ? safe : [primarySlug];
};

export const getSortData = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem
): SortRule[] => {
  return (
    formItem.params?.sort_data ??
    field.params?.select_record_modal_sort_data ??
    field.params?.sort_data
  );
};

/**
 * Returns the slug of the backlink (symmetric) field that has to be inspected to know whether a
 * linked record is already taken, or undefined when the field allows duplicates.
 *
 * A record of the linked application can only be linked once when the backlink field is single
 * entry — either because the field mirrors an existing one, or because it links the application to
 * itself (in which case the backlink is the field itself). Self linking without a backlink keeps no
 * symmetry at all, so nothing can be excluded.
 *
 * Mirrors `selectRecordsToExcludeFromSelect` in the grid (frontend repo) so that the form and the
 * grid hide the same records.
 */
export const getBacklinkFieldSlug = (
  field: FormConfigApplicationStructureItem
): string | undefined => {
  const isMirroredField = !!field.params?.autocreated_symmetric_field;
  const isLinkedToItself = field.params?.symmetric_field === field.slug;

  if (isLinkedToItself && field.params?.do_not_create_backlink) return undefined;
  if (!isMirroredField && !isLinkedToItself) return undefined;

  return field.params?.symmetric_field;
};

interface FilterOutAlreadyLinkedRecordsParams {
  records: SimpleRecord[];
  field: FormConfigApplicationStructureItem;
  linkedApplicationFields?: {
    [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
  };
}

/**
 * Drops the records that are already linked through a single entry backlink field, so that the
 * select modal / dropdown never offers a record that cannot accept another link.
 */
export const filterOutAlreadyLinkedRecords = ({
  records,
  field,
  linkedApplicationFields,
}: FilterOutAlreadyLinkedRecordsParams): SimpleRecord[] => {
  const backlinkSlug = getBacklinkFieldSlug(field);

  if (!backlinkSlug) return records;

  // For a self linking field the backlink is the field itself, whose params the form config
  // already carries — no need to wait for the linked application structure to load.
  const backlinkEntriesAllowed =
    backlinkSlug === field.slug
      ? field.params?.entries_allowed
      : linkedApplicationFields?.[backlinkSlug]?.params?.entries_allowed;

  if (backlinkEntriesAllowed !== "single") return records;

  return records.filter((record) => {
    const backlinkValue = record[backlinkSlug];

    return Array.isArray(backlinkValue) ? backlinkValue.length === 0 : !backlinkValue;
  });
};

export const getFieldsInUse = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem,
  filterData?: Filter,
  isTable: boolean = false
): Set<string> => {
  const fieldsInUse = new Set<string>();

  const backlinkSlug = getBacklinkFieldSlug(field);
  if (backlinkSlug) {
    fieldsInUse.add(backlinkSlug);
  }

  const recordSelectType = formItem.params?.record_select_type;

  fieldsInUse.add("title");

  if (isTable) {
    const tableFields = formItem.params.table_visible_fields ?? [];
    tableFields.forEach((slug) => fieldsInUse.add(slug));
  }

  if (isTable || recordSelectType !== "dropdown") {
    const visibleFields = getVisibleFields(formItem, field);
    visibleFields.forEach((field: string) => fieldsInUse.add(field));

    const coverImageField =
      formItem.params?.cover_image_field ??
      (typeof field.params === "object" && "cover_image_field" in field.params
        ? (field.params as { cover_image_field?: string }).cover_image_field
        : undefined);

    if (coverImageField) {
      fieldsInUse.add(coverImageField);
    }
  }

  if (filterData) {
    filterData.fields.forEach((filterField) => {
      fieldsInUse.add(filterField.field);
    });
  }

  const sortData = getSortData(formItem, field);
  if (sortData) {
    sortData.forEach((sortField: { field: string }) => {
      fieldsInUse.add(sortField.field);
    });
  }

  return fieldsInUse;
};
